import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Tabs, Tab } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { PermissionRoles, PermissionsTypes } from 'enum/permissionsType'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import Button from 'design-system/button'
import { IMainState } from 'store/states'
import { IModeratorPermissionsModel } from 'models/moderatorPermissions.model'
import { IBasicProfile } from 'models/lmsProfile.model'
import { ICourseNameList } from 'models/course.model'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import * as fromActions from 'store/actions/participants.action'
import { WarningToast } from 'components/ui/toast/toastsTemplates'
import { PermitableTypes } from 'enum/permissionsTypes.enum'
import PermissionsTable from './permissions-table/PermissionsTable'
import { emptyField } from './constants'
import styles from './PermissionsManagementModal.module.scss'

const PREFIX = 'configuration.user.permissionsManagementModal'

interface IPermissionsManagementModal {
  show: boolean
  onClose: any
  userId: number
}

const PermissionsManagementModal: React.FC<IPermissionsManagementModal> = ({
  show,
  onClose,
  userId,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { admin, moderator } = PermissionRoles
  const { profile, course } = PermissionsTypes
  const [permissionsTypeSelected, setPermissionsTypeSelected] = useState(
    admin as any
  )
  const [profiles, setProfiles] = useState([] as any[])
  const [courses, setCourses] = useState([] as any[])
  const [profilePermissions, setProfilesPermissions] = useState(
    [] as IModeratorPermissionsModel[]
  )

  const [coursePermissions, setCoursePermissions] = useState(
    [] as IModeratorPermissionsModel[]
  )
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('1')
  const [profilesListLoading, setProfilesListLoading] = useState(true)
  const [coursesListLoading, setCoursesListLoading] = useState(true)
  const { roles } = useSelector(
    ({ participants: { participant } }: IMainState) => participant
  )
  const { allProfilesList: lmsProfileList, loading: profilesLoading } =
    useSelector(({ lmsProfiles }: IMainState) => lmsProfiles)
  const { nameList: coursesList, loading: coursesLoading } = useSelector(
    ({ courses }: IMainState) => courses
  )

  const permissionsList = useRef<IModeratorPermissionsModel[]>([])

  useEffect(() => {
    // !loading significa que ambas listas (perfiles y cursos) terminaron de cargar
    if (roles && !loading) {
      setPermissionsTypeSelected(roles.roleId || 1)
      // Limpio y ordeno la data
      const aux = roles.permissions.map(
        (
          {
            id,
            permitableId,
            canAnswer,
            canDownload,
            canEdit,
            canMonitor,
            canRequest,
            permitableType,
            canMonitoringAreas,
            canManageCourses,
          }: any,
          index: number
        ): IModeratorPermissionsModel => {
          return {
            id,
            index,
            type:
              permitableType === PermitableTypes.LmsProfile ? profile : course,
            selected: getSelected(
              permitableId,
              permitableType === PermitableTypes.LmsProfile ? profiles : courses
            ),
            seeMonitor: canMonitor,
            downloadReports: canDownload,
            editCourse: canEdit,
            chat: canAnswer,
            approveRequests: canRequest,
            monitoringAreas: canMonitoringAreas,
            manageCourses: canManageCourses,
          }
        }
      )
      permissionsList.current = aux
      // Separo en 2 arreglos
      const [profilePermissions, coursePermissions] = aux.reduce(
        ([profiles, courses]: any, current: any) =>
          current.type === profile
            ? [[...profiles, current], courses]
            : [profiles, [...courses, current]],
        [[] as any, [] as any]
      )
      setProfilesPermissions(profilePermissions)
      setCoursePermissions(coursePermissions)
    }
  }, [roles, loading, profile, course, profiles, courses])

  const getSelected = (id: number, array: any[]) => {
    return array.find(({ value }: any) => value === id)
  }

  useEffect(() => {
    if (!coursesLoading) {
      setCourses(
        coursesList.map(({ id, name, profiles }: ICourseNameList) => {
          const auxProfiles = profiles
            .slice(0, 3)
            .map(({ name }: any) => `${name}`)
            .join(', ')
          const tooltip = profiles.map(({ name }: any) => `${name}`).join(', ')
          return {
            value: id,
            label: name,
            child: auxProfiles || '',
            tooltip: tooltip || '',
          }
        })
      )
      setCoursesListLoading(false)
    }
  }, [coursesList, coursesLoading])

  useEffect(() => {
    if (!coursesListLoading && !profilesLoading) {
      setProfiles(
        lmsProfileList.map(({ id, name, color }: IBasicProfile) => ({
          value: id,
          label: name,
          color,
        }))
      )
      setProfilesListLoading(false)
    }
  }, [coursesListLoading, lmsProfileList, profilesLoading])

  useEffect(() => {
    if (!profilesListLoading && !coursesListLoading) setLoading(false)
  }, [profilesListLoading, coursesListLoading])

  const setData = (
    index: number,
    inputName: string,
    value: any,
    variant: PermissionsTypes
  ) => {
    function updatePermissionsList(permissions: IModeratorPermissionsModel[]) {
      const aux = permissions.map((row) => ({ ...row })) //DeepClone
      let exist = false
      let prevValue = { ...emptyField }
      if (!aux[index]) {
        aux[index] = { ...emptyField, index }
      } else {
        exist = true
        prevValue = { ...aux[index] }
      }
      // @ts-ignore
      aux[index][inputName] = value

      if (exist) {
        const listIndex = permissionsList.current.findIndex(
          ({ selected, type }: any) => {
            const { value } = selected ?? {}
            return value === prevValue.selected?.value && type === variant
          }
        )
        if (listIndex >= 0)
          permissionsList.current[listIndex] = { ...aux[index], type: variant }
      } else {
        permissionsList.current.push({ ...aux[index], type: variant })
      }
      return aux
    }
    if (variant === profile)
      setProfilesPermissions((prev) => updatePermissionsList(prev))
    else setCoursePermissions((prev) => updatePermissionsList(prev))
  }

  const onDelete = (index: number, variant: string) => {
    const aux =
      variant === profile
        ? profilePermissions.slice()
        : coursePermissions.slice()
    const listIndex = permissionsList.current.findIndex(
      ({ selected, type }: any) => {
        const { value } = selected ?? {}
        return value === aux[index].selected?.value && variant === type
      }
    )

    aux.splice(index, 1)
    variant === profile
      ? setProfilesPermissions(aux)
      : setCoursePermissions(aux)

    if (listIndex >= 0) {
      const permission = permissionsList.current[listIndex]
      if (permission.id && permission.id >= 0) {
        permissionsList.current[listIndex]._destroy = true
      } else {
        permissionsList.current.splice(listIndex, 1)
      }
    }
  }

  const noPermissionDefined = () =>
    WarningToast(t(`${PREFIX}.toasts.noPermissionDefined`))

  const onSubmit = () => {
    const permissions = permissionsList.current || []
    let canContinue = true
    const user = {
      roleId: permissionsTypeSelected,
    } as any

    if (permissionsTypeSelected === moderator) {
      const withPermissions = permissions.filter((p) => !p._destroy).length > 0
      if (!withPermissions) {
        noPermissionDefined()
        return
      }
      user.permissionsAttributes = permissions.map(
        ({
          id = '',
          chat,
          downloadReports,
          editCourse,
          seeMonitor,
          approveRequests,
          selected,
          type,
          monitoringAreas,
          manageCourses,
          _destroy = false,
        }) => {
          const { value } = { ...selected }
          if (
            !value ||
            (!chat &&
              !downloadReports &&
              !editCourse &&
              !seeMonitor &&
              !approveRequests &&
              !monitoringAreas &&
              !manageCourses &&
              !_destroy)
          )
            canContinue = false

          return {
            id,
            canAnswer: chat,
            canDownload: downloadReports,
            canEdit: editCourse,
            canMonitor: seeMonitor,
            canRequest: approveRequests,
            canMonitoringAreas: monitoringAreas,
            canManageCourses: manageCourses,
            permitableType:
              type === profile
                ? PermitableTypes.LmsProfile
                : PermitableTypes.Course,
            permitableId: value,
            _destroy,
          }
        }
      )
    }

    if (canContinue) {
      dispatch({
        type: fromActions.PARTICIPANT_EDIT_ROLE,
        payload: { user, userId },
      })
      onClose()
    } else {
      noPermissionDefined()
    }
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className={styles.modalContainer}
    >
      <Modal.Header
        translate="yes"
        className={`pt-3 mt-1 px-4 ${styles.modalHeader}`}
        closeButton
      >
        <div className={styles.title}>{t(`${PREFIX}.title`)}</div>
      </Modal.Header>
      <Modal.Body className="pt-1 px-4">
        <div className={`${styles.subtitle} mt-1 mb-2`}>
          {t(`${PREFIX}.chooseTypeOfPermission`)}
        </div>
        <div>
          <div className="mb-1">
            <CheckBoxRadioButtonInput
              key={'permisionType'}
              name={'PermisionTypeAdmin'}
              value={true}
              checked={permissionsTypeSelected === admin}
              inline={true}
              type={'radio'}
              label={t(`${PREFIX}.admin.title`)}
              onChange={() => setPermissionsTypeSelected(admin)}
            />
            <div className={`${styles.description} mt-1`}>
              {t(`${PREFIX}.admin.description`)}
            </div>
          </div>
          <div className="mt-3">
            <CheckBoxRadioButtonInput
              key={'permisionType'}
              name={'PermisionTypeModerator'}
              value={true}
              checked={permissionsTypeSelected === moderator}
              inline={true}
              type={'radio'}
              label={t(`${PREFIX}.moderator.title`)}
              onChange={() => setPermissionsTypeSelected(moderator)}
            />
            <div className={`${styles.description} mt-1`}>
              {t(`${PREFIX}.moderator.description`)}
            </div>
          </div>
        </div>
        {loading ? (
          <Loader sizes={LoaderSizes.ExtraSmall} color />
        ) : permissionsTypeSelected === moderator ? (
          <div className="mt-4">
            <Tabs
              defaultActiveKey={'profiles'}
              onSelect={(eventKey: any) => setActiveTab(eventKey)}
              activeKey={activeTab}
              id="some-id"
              className={`${styles.tabs} mb-3`}
            >
              <Tab eventKey={'1'} title={t(`${PREFIX}.tabs.profile.title`)}>
                {activeTab === '1' && (
                  <PermissionsTable
                    profiles={profiles}
                    variant={PermissionsTypes.profile}
                    setData={({ index, inputName, value }: any) =>
                      setData(index, inputName, value, profile)
                    }
                    data={profilePermissions}
                    onDelete={(index: number) => onDelete(index, profile)}
                  />
                )}
              </Tab>
              <Tab eventKey={'2'} title={t(`${PREFIX}.tabs.course.title`)}>
                {activeTab === '2' && (
                  <PermissionsTable
                    courses={courses}
                    variant={PermissionsTypes.course}
                    setData={({ index, inputName, value }: any) =>
                      setData(index, inputName, value, course)
                    }
                    data={coursePermissions}
                    onDelete={(index: number) => onDelete(index, course)}
                  />
                )}
              </Tab>
            </Tabs>
          </div>
        ) : (
          <div className="mb-5" />
        )}
        <div className={styles.modalFooter}>
          <div>
            <Button
              className="mr-0"
              variant={ButtonVariant.Secondary}
              size={ButtonSizes.Small}
              onClick={onClose}
            >
              {t(`${PREFIX}.buttons.cancel`)}
            </Button>
            <Button
              className="mr-0"
              onClick={onSubmit}
              size={ButtonSizes.Small}
            >
              {t(`${PREFIX}.buttons.accept`)}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default PermissionsManagementModal
